import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, fromEvent, merge, interval } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';
import { SessionService } from './session.service';
import { UsersService } from './users.service';
import Swal from 'sweetalert2';

@Injectable({
	providedIn: 'root'
})
export class InactivityService {
	private tokenRefreshInterval = 15 * 60 * 1000;
	private warningTime = 13 * 60 * 1000;
	private inactivityTime = 15 * 60 * 1000;

	private inactivityTimer$ = new Subject<void>();
	private destroy$ = new Subject<void>();

	private isWarningShown = false;
	private refreshIntervalSubscription: any = null;
	private refreshTimeout: any = null;
	private warningTimeout: any = null;
	private logoutTimeout: any = null;

	started = false;

	constructor(
		private ngZone: NgZone,
		private router: Router,
		private localStorage: LocalStorageService,
		private userService: UsersService,
		private sessionService: SessionService
	) {}

	startWatching(): void {
		console.log('🚀 startWatching ejecutado');

		if (this.started) {
			console.log('⛔ ya iniciado');
			return;
		}
		this.started = true;

		if (this.isTokenExpired()) {
			console.warn('[WATCH BLOQUEADO - TOKEN INVÁLIDO]');
			this.stopWatching();
			this.sessionService.clearSessionHard('session_expired');
			return;
		}

		// Recrear subjects en caso de que hayan sido completados por un ciclo anterior
		this.destroy$ = new Subject<void>();
		this.inactivityTimer$ = new Subject<void>();

		const userActivity$ = merge(
			fromEvent(document, 'mousedown'),
			fromEvent(document, 'keypress'),
			fromEvent(document, 'scroll'),
			fromEvent(document, 'touchstart'),
			fromEvent(document, 'click'),
			fromEvent(document, 'mousemove')
		);

		this.ngZone.runOutsideAngular(() => {
			userActivity$
				.pipe(
					debounceTime(1000),
					takeUntil(this.destroy$)
				)
				.subscribe(() => {
					this.ngZone.run(() => {
						this.onUserActivity();
					});
				});
		});

		this.startTokenRefreshTimer();
		this.resetInactivityTimers();
	}

	private onUserActivity(): void {
		this.resetInactivityTimers();
	}

	private startTokenRefreshTimer(): void {
		if (this.refreshIntervalSubscription) {
			this.refreshIntervalSubscription.unsubscribe();
		}
		if (this.refreshTimeout) clearTimeout(this.refreshTimeout);

		const lastRefreshStr = this.localStorage.getItem('ROD-last-token-refresh');
		const now = Date.now();

		let initialDelay = this.tokenRefreshInterval;

		if (lastRefreshStr) {
			const lastRefresh = parseInt(lastRefreshStr);
			const timeSinceLastRefresh = now - lastRefresh;
			const timeUntilNextRefresh = this.tokenRefreshInterval - timeSinceLastRefresh;

			// FIX: Math.max(0, ...) evita delays negativos si el refresh ya debió haberse ejecutado
			initialDelay = Math.max(0, timeUntilNextRefresh);
		}

		// Fuera de la zona: un timer pendiente dentro de ella impide que la app quede estable
		// y mantiene activo el cache HTTP de hidratacion (GET repetidos responden datos viejos).
		this.ngZone.runOutsideAngular(() => {
			this.refreshTimeout = setTimeout(() => {
				this.ngZone.run(() => this.refreshToken());

				this.refreshIntervalSubscription = interval(this.tokenRefreshInterval)
					.pipe(takeUntil(this.destroy$))
					.subscribe(() => this.ngZone.run(() => this.refreshToken()));

			}, initialDelay);
		});
	}

	private resetInactivityTimers(): void {
		if (this.warningTimeout) clearTimeout(this.warningTimeout);
		if (this.logoutTimeout) clearTimeout(this.logoutTimeout);

		this.inactivityTimer$.next();
		this.isWarningShown = false;

		this.ngZone.runOutsideAngular(() => {
			this.warningTimeout = setTimeout(() => {
				this.ngZone.run(() => this.showWarning());
			}, this.warningTime);
		});

		this.ngZone.runOutsideAngular(() => {
			this.logoutTimeout = setTimeout(() => {
				this.ngZone.run(() => this.logout());
			}, this.inactivityTime);
		});
	}

	private refreshToken(): void {
		const user = this.localStorage.getItem('ROD-logged.user');
		if (!user) return;

		if (this.isTokenExpired()) {
			console.error('[JWT EXPIRADO - REFRESH BLOQUEADO]');
			this.stopWatching();
			this.sessionService.clearSessionHard('session_expired');
			return;
		}

		this.userService.refreshToken().subscribe({
			next: (response: any) => {
				this.localStorage.setItem('ROD-last-token-refresh', Date.now().toString());

				const userData = JSON.parse(user);
				userData.token = response.data.token;
				userData.expiration = response.data.expiration;
				this.localStorage.setItem('ROD-logged.user', JSON.stringify(userData));
			},
			error: (error: any) => {
				console.error('❌ Error al refrescar token:', error);
				console.error('  Status:', error.status);
				console.error('  Hora:', new Date().toLocaleTimeString());

				if (error.status === 401 || error.status === 403) {
					// FIX: llamamos logout() aquí, que muestra el Swal con el mensaje correcto.
					// El interceptor detecta isRefreshRequest y NO muestra un segundo Swal,
					// así evitamos duplicidad de mensajes.
					this.logout('refresh_failed');
				}
				// Para otros errores (red, 5xx) no cerramos sesión — el usuario puede seguir.
			}
		});
	}

	private showWarning(): void {
		if (this.isWarningShown) return;
		this.isWarningShown = true;

		Swal.fire({
			title: '¿Sigues ahí?',
			html: '<div>Tu sesión expirará en 2 minutos por inactividad.</div>',
			icon: 'warning',
			confirmButtonText: 'Continuar activo',
			showCancelButton: true,
			cancelButtonText: 'Cerrar sesión',
			buttonsStyling: false,
			customClass: {
				confirmButton: 'btn btn-primary',
				cancelButton: 'btn btn-secondary ms-2',
				popup: 'swal-wider'
			},
			allowOutsideClick: false
		}).then((result) => {
			if (result.isConfirmed) {
				this.resetInactivityTimers();
			} else {
				this.logout('inactivity');
			}
		});
	}

	private logout(reason: 'inactivity' | 'refresh_failed' | 'session_expired' = 'inactivity'): void {
		sessionStorage.setItem('LOGOUT_REASON', reason);
		Swal.close();

		const titles: Record<string, string> = {
			refresh_failed: 'No se pudo actualizar la sesión',
			session_expired: 'Sesión expirada',
			inactivity: 'Sesión cerrada por inactividad',
		};

		const messages: Record<string, string> = {
			refresh_failed: 'No se pudo renovar la sesión. Inicia sesión nuevamente.',
			session_expired: 'Tu sesión expiró. Inicia sesión nuevamente.',
			inactivity: 'Tu sesión se cerró por inactividad.',
		};

		Swal.fire({
			title: titles[reason],
			html: `<div>${messages[reason]}</div>`,
			icon: 'warning',
			confirmButtonText: 'Ok',
			buttonsStyling: false,
			customClass: { confirmButton: 'btn btn-primary' }
		}).then(() => {
			this.clearSession();
		});
	}

	private clearSession(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.inactivityTimer$.next();
		this.inactivityTimer$.complete();

		if (this.refreshIntervalSubscription) this.refreshIntervalSubscription.unsubscribe();
		if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
		if (this.warningTimeout) clearTimeout(this.warningTimeout);
		if (this.logoutTimeout) clearTimeout(this.logoutTimeout);

		this.started = false;

		this.localStorage.removeItem('ROD-logged.user');
		this.localStorage.removeItem('ROD-logged.userData');
		this.localStorage.removeItem('ROD-simulation.clientType');
		this.localStorage.removeItem('ROD-simulation.location');
		this.localStorage.removeItem('ROD-simulation');
		this.localStorage.removeItem('ROD-basketId');
		this.localStorage.removeItem('ROD-simulation.isSP');
		this.localStorage.removeItem('ROD-last-token-refresh');

		UsersService.loggedOut.emit(true);
		location.href = '/conta/login';
	}

	stopWatching(): void {
		this.destroy$.next();
		this.destroy$.complete();    // FIX: completar para liberar memoria del observable
		this.inactivityTimer$.next();

		if (this.refreshIntervalSubscription) this.refreshIntervalSubscription.unsubscribe();
		if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
		if (this.warningTimeout) clearTimeout(this.warningTimeout);
		if (this.logoutTimeout) clearTimeout(this.logoutTimeout);

		this.started = false;        // FIX: resetear para permitir reinicio limpio con startWatching()
	}

	isTokenExpiringSoon(): boolean {
		const user = this.localStorage.getItem('ROD-logged.user');
		if (!user) return false;

		try {
			const userData = JSON.parse(user);
			if (!userData.expiration) return false;

			const expirationDate = new Date(userData.expiration);
			const now = new Date();
			const minutesRemaining = (expirationDate.getTime() - now.getTime()) / 1000 / 60;

			return minutesRemaining <= 5 && minutesRemaining > 0;
		} catch {
			return false;
		}
	}

	isTokenExpired(): boolean {
		const user = this.localStorage.getItem('ROD-logged.user');
		if (!user) return true;

		try {
			const { token } = JSON.parse(user);
			if (!token) return true;

			const payload = JSON.parse(atob(token.split('.')[1]));
			if (!payload?.exp) return true;

			const now = Math.floor(Date.now() / 1000);
			return now >= payload.exp;
		} catch {
			return true;
		}
	}
}